import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {Component, Inject, Input, ViewEncapsulation} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';

import uuid from 'short-unique-id';
import {GlobalService} from '../../../../../../global/global.service';
import {ICustomColumnService, ICustomColumnServiceToken} from '../../../../../../interfaces/custom-columns';
import {DocumentFieldService} from '../../../../../pages/settings/documentfields/documentfield.service';
import {IMetadataItemDto} from '../../../../../pages/settings/documentfields/metadata-item.dto';
import {IGetCategoriesItemDto} from '../../../../../pages/categories.dto';
import {IColumnSettings} from './column-settings';
import {SharedStoreFacade} from '../../../../../shared/shared/store/shared-store.facade';
import {combineLatest} from 'rxjs';
import {take} from 'rxjs/operators';
import * as categoriesSelectors from '../../../../../../store/selectors/categories.selector';
import {Store} from '@ngrx/store';


@Component({
    selector: 'app-custom-columns-settings',
    templateUrl: './custom-columns-settings.component.html',
    styleUrl: './custom-columns-settings.component.scss',

    encapsulation: ViewEncapsulation.None
})
export class CustomColumnsSettingsComponent {
    private uuid = new uuid({ length: 6, dictionary: 'number' });

    @Input() public customColumns: IColumnSettings[] = [];
    public _customColumnSearchLoader: boolean = false;
    public _searchedColumns: Array<IMetadataItemDto | IGetCategoriesItemDto> = [];
    public _columnKeyWord: string = 'meta_data_name';
    public _categories: IGetCategoriesItemDto[] = [];


    constructor(public modal: NgbActiveModal,
        private globalService: GlobalService,
        private _documentFieldService: DocumentFieldService,
        private _translocoService: TranslocoService,
        private _sharedStoreFacade: SharedStoreFacade,
        private _store: Store,
        @Inject(ICustomColumnServiceToken) private _customColumnService: ICustomColumnService
    ) { 
        this.loadParentCategories();
    }


    /**
     * Load all categories from global store (both parent and child categories)
     */
    loadParentCategories() {
        // Subscribe to global categories store
this._store.select(categoriesSelectors.allCategories).pipe(take(1)).subscribe({
            next: (categories) => {
                this._categories = categories || [];
                // Include all categories (both parent and child) for search
                // Don't filter - let users search for any category
            },
            error: (error) => {
                console.error('Error loading categories from store:', error);
            }
        });
    }

    /**
     * Search column
     * @param q
     * @returns
     */
    searchColumn(q: string = '') {
        if (q === '') {
            this._searchedColumns = [];
            return;
        }
        this.fetchDocumentfields(1, q);
    }


    /**
     * On clear custom column search remove searched data
     * @param $event
     */
    clearSearch() {
        this._searchedColumns = [];
    }


    /**
     * Fetch document fields
     * @param page
     * @param query
     */
    fetchDocumentfields(page: number = 1, query: string = '') {
        this._customColumnSearchLoader = true;
        
        // Search document fields
        this._documentFieldService.documentfields(page, query, '&section=document_field&all')
            .subscribe({
                next: (response) => {
                    let searchedColumns: Array<IMetadataItemDto | IGetCategoriesItemDto> = (response['data'] ? response['data'] : response) as IMetadataItemDto[] || [];
                    
                    const columns = this._customColumnService.getCustomColumns().filter((item) => {
                        return (
                          item['label'].toLowerCase().includes(query.toLowerCase())
                        );
                      })?.map((item: IColumnSettings) => ({
                        cid: item?.cid ?? '', // Custom id of default columns
                        section: "default",
                        id: 0,
                        doc_id: null,
                        meta_data_name: this._translocoService.translate(item?.label),
                        field_name: item?.field_name
                      }));

                    if (columns?.length) {
                        // Map columns to IMetadataItemDto shape with default values for missing properties
                        const mappedColumns: IMetadataItemDto[] = columns.map((item: any) => ({
                            api_attribute: '',
                            created_at: '',
                            id: item.id ?? 0,
                            is_required: false,
                            meta_data_name: item.meta_data_name ?? '',
                            section: item.section ?? 'default',
                            doc_id: item.doc_id ?? null,
                            field_name: item.field_name ?? '',
                            ...item
                        }));
                        searchedColumns = [...mappedColumns, ...searchedColumns];
                    }

                    // Search child categories (categories that have a parent_id)
                    const matchedCategories = this._categories.filter(category => 
                        category.category_name?.toLowerCase().includes(query.toLowerCase())
                    ).map(category => ({
                        ...category,
                        section: 'category',
                        meta_data_name: category.category_name,
                        field_name: `category_${category.id}`
                    }));

                    searchedColumns = [...searchedColumns, ...matchedCategories];
                    this._searchedColumns = searchedColumns;
                    this._customColumnSearchLoader = false;
                },
                error: (error) => {
                    this.globalService.toastrError(error);
                    this._customColumnSearchLoader = false;
                }
            });
    }




    /**
     * On delete custom column
     * @param column
     */
    deleteColumn(column: IColumnSettings = {} as IColumnSettings) {
        this.customColumns = this.customColumns.filter((item) => item.uuid !== column.uuid);
    }


    /**
     * Get drop event
     * @param event
     */
    drop(event: CdkDragDrop<string[]>) {
        const copiedItems = this.customColumns.slice();
        moveItemInArray(copiedItems, event.previousIndex, event.currentIndex);
        this.customColumns = copiedItems;
        this.customColumns.forEach((item: IColumnSettings, index: number) => {
            item.order = index;
            return item;
        });
    }


    /**
     * Add new custom column
     * @param $item
     */
    selectColumn($item) {
const isCategory = $item?.section === 'category';
        const customColumnObject = {
            uuid: this.uuid(),
            status: 1,
            cid: $item?.cid ?? '', // Custom id of default columns
            order: (this.customColumns.length + 1),
            type: ($item?.section ?? "default"),
            doc_id: ($item?.id ?? null),
            field_name: ($item?.field_name ?? $item?.meta_data_name),
            label: ($item?.label ?? $item?.meta_data_name),
            details: $item,
            is_category_column: isCategory
        } as IColumnSettings;
        
        const isColumnExists = this.customColumns?.some((column) => {
            if($item?.id != 0) {
                return column?.doc_id == $item?.id;
            }
            return (column?.cid != null && column?.cid == $item?.cid)
        });
        
        if (isColumnExists) {
            this.globalService.toastrError(this._translocoService.translate('Custom columns toaster message - (Contract control - custom column modal) - Column already exists'));
            return;
        }
        this.customColumns.push(customColumnObject);
    }


    /**
     * Update custom column setting
     * @default
     */
    updateCustomColumnSettings() {
        this.modal.close({ column_settings: this.customColumns });
    }
}
